import React, { createContext, useState, useContext, useEffect } from 'react';

type AlertType = 'error' | 'warning' | 'success';

interface AlertContextProps {
  showAlert: (type: AlertType, message: string, duration?: number) => void;
}

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert muss innerhalb eines AlertProviders verwendet werden');
  }
  return context;
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<AlertType>('success');
  const [show, setShow] = useState(false);
  const [duration, setDuration] = useState(3000);

  const showAlert = (type: AlertType, message: string, duration = 3000) => {
    setAlertType(type);
    setAlertMessage(message);
    setDuration(duration);
    setShow(true);
  };

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setShow(false), duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {show && (
        <div
          className={`fixed bottom-20 left-1/2 transform -translate-x-1/2 p-6 min-w-96  text-white rounded-xl flex items-center justify-center ${getBackgroundClass(alertType)}`}
        >
          {alertMessage}
        </div>
      )}
    </AlertContext.Provider>
  );
};

function getBackgroundClass(type: AlertType) {
  switch (type) {
    case 'error':
      return 'bg-red-700';
    case 'warning':
      return 'bg-yellow-700';
    case 'success':
      return 'bg-green-700';
    default:
      return 'bg-gray-500';
  }
}
